import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { GraduationCap, AlertCircle, ArrowLeft, CheckCircle2 } from 'lucide-react';
import * as api from '../utils/api';

export default function SignUpPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'teacher' | 'student'>('student');
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  // Available course options
  const availableCourses = [
    'AP Calculus AB',
    'AP Calculus BC',
    'AP Physics 1',
    'AP Physics 2',
    'AP Physics C',
    'AP Chemistry',
    'AP Biology',
    'AP Statistics',
    'AP Computer Science A',
    'AP Computer Science Principles'
  ];

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate(`/${user.role}`);
    }
  }, [user, navigate]);

  const toggleCourse = (course: string) => {
    setSelectedCourses(prev => 
      prev.includes(course) 
        ? prev.filter(c => c !== course)
        : [...prev, course]
    );
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please enter a valid email address');
      }

      // Validate name
      if (name.trim().length < 2) {
        throw new Error('Please enter your full name');
      }

      // Generate a random password
      const password = generatePassword();
      setGeneratedPassword(password);

      // Register the user via API
      await api.register(email.trim(), password, name.trim(), role);

      // Show success message
      setSuccess(true);

      // In a real application, we would send an email here
      console.log('User registered:', { email, name, role, courses: selectedCourses });
      console.log('Generated password:', password);

    } catch (err: any) {
      console.error('Registration error:', err.message);
      setError(err.message || 'Failed to register');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-green-600 rounded-full">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl text-green-600">Registration Successful!</CardTitle>
            <CardDescription>Your account has been created</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Success message */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-900 font-medium mb-2">
                Welcome to AP Course Improvement!
              </p>
              <p className="text-xs text-green-800">
                Your account has been created successfully. In a production environment, 
                you would receive a confirmation email with your password.
              </p>
            </div>

            {/* Temporary password display (for demo purposes only) */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-900 font-medium mb-2">
                Your Login Credentials:
              </p>
              <div className="space-y-2 text-sm text-blue-800">
                <p><strong>Email:</strong> {email}</p>
                <p><strong>Password:</strong> <code className="bg-blue-100 px-2 py-1 rounded">{generatedPassword}</code></p>
              </div>
              <p className="text-xs text-blue-700 mt-3">
                Please save these credentials. In a real application, you would receive 
                these via email.
              </p>
            </div>

            {/* Selected courses */}
            {selectedCourses.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-700 font-medium mb-2">
                  Your Interested Courses:
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedCourses.map(course => (
                    <span 
                      key={course}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
                    >
                      {course}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="pt-4">
              <Button 
                className="w-full" 
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-blue-600 rounded-full">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">Create Your Account</CardTitle>
          <CardDescription>Join AP Course Improvement to enhance your learning</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Info notice */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 font-medium">Account Creation</p>
              <p className="text-xs text-blue-800 mt-1">
                After registration, you'll receive your login credentials. In this demo, 
                they'll be displayed on screen. In production, they would be sent via email.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <Label>I am a: *</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setRole('student')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    role === 'student'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div className={`text-lg font-medium ${
                      role === 'student' ? 'text-blue-700' : 'text-gray-700'
                    }`}>
                      Student
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Take quizzes and track progress
                    </p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setRole('teacher')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    role === 'teacher'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div className={`text-lg font-medium ${
                      role === 'teacher' ? 'text-blue-700' : 'text-gray-700'
                    }`}>
                      Teacher
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Create courses and quizzes
                    </p>
                  </div>
                </button>
              </div>
            </div>

            {/* Interested Courses */}
            <div className="space-y-2">
              <Label>Interested Courses (Optional)</Label>
              <p className="text-sm text-gray-500">
                Select the courses you're interested in
              </p>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 border rounded-lg">
                {availableCourses.map(course => (
                  <button
                    key={course}
                    type="button"
                    onClick={() => toggleCourse(course)}
                    className={`p-3 rounded-md text-sm text-left transition-all ${
                      selectedCourses.includes(course)
                        ? 'bg-blue-100 border-2 border-blue-600 text-blue-700'
                        : 'bg-gray-50 border-2 border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {course}
                  </button>
                ))}
              </div>
              {selectedCourses.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {selectedCourses.length} course{selectedCourses.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="space-y-3">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>

              {/* Back to login */}
              <Button 
                type="button"
                variant="outline" 
                className="w-full" 
                onClick={() => navigate('/login')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}